import { useMemo } from 'react'

import { Box, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { AlertCircle, CheckCircle } from '@factorialco/f0-react/icons/app'
import {
  ShiftManagementRolesRequirementsFragment,
  ShiftManagementTreeRolesRequirementsFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'

interface Props {
  unassignedShift: UnassignedShift
  roles: ShiftManagementRolesRequirementsFragment[]
  treeRoles: ShiftManagementTreeRolesRequirementsFragment[] | undefined
  originalCounter: number
  totalAssignedShifts: number
}

const RolesDetails = ({
  unassignedShift,
  roles,
  treeRoles,
  originalCounter,
  totalAssignedShifts,
}: Props) => {
  const [isTooltipOpen, setIsTooltipOpen] = useToggle(false)
  const icon = originalCounter > totalAssignedShifts ? AlertCircle : CheckCircle
  const iconColor = originalCounter > totalAssignedShifts ? 'warning' : 'positive'

  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasLegacyJobCatalogFeature = hasFeatureFlag('legacy_job_catalog')

  // Get role names based on feature flag
  const allRoleNames = useMemo(() => {
    if (!unassignedShift) return []

    if (!hasLegacyJobCatalogFeature && !treeRoles) return []
    if (hasLegacyJobCatalogFeature && roles?.length === 0) return []
    if (!hasLegacyJobCatalogFeature) {
      // Use tree roles (new schema)
      const treeRolesMap = new Map<string, string>()
      treeRoles?.forEach((role) => {
        if (role.name) {
          treeRolesMap.set(role.uuid, role.name)
        }
      })
      return (
        unassignedShift.jobCatalogTreeRoleUuids
          ?.map((uuid) => treeRolesMap.get(uuid))
          .filter((name): name is string => Boolean(name)) || []
      )
    } else {
      // Use old roles (legacy schema)
      const rolesMap = new Map<string, string>()
      roles.forEach((role) => rolesMap.set(String(role.id), role.name))

      return unassignedShift.jobCatalogRolesIds
        .map((id) => rolesMap.get(String(id)))
        .filter((name): name is string => Boolean(name))
    }
  }, [
    hasLegacyJobCatalogFeature,
    unassignedShift.jobCatalogRolesIds,
    unassignedShift.jobCatalogTreeRoleUuids,
    roles,
    treeRoles,
  ])

  const isLoadingRoles = useMemo(() => {
    if (!hasLegacyJobCatalogFeature) {
      return !treeRoles
    }
    return false
  }, [hasLegacyJobCatalogFeature])

  const buildLabel = () => {
    return allRoleNames.join(', ')
  }

  const hasRoles = allRoleNames.length > 0

  return (
    <Box flexDirection="row" justifyContent="flexStart" alignItems="center" gap="s2">
      <F0Icon icon={icon} size="sm" color={iconColor} />

      <Text size="100" weight="medium" letterSpacing="s2">
        {totalAssignedShifts}/{originalCounter}
      </Text>

      {hasRoles && (
        <Text.Normal ellipsis size="100" weight="medium">
          {allRoleNames[0]}
        </Text.Normal>
      )}

      {allRoleNames.length > 1 ? (
        <Box position="relative">
          <Tooltip.Alt
            open={isTooltipOpen}
            attachment="bottom center"
            label={buildLabel()}
            toggle={setIsTooltipOpen}
          >
            <Text.Normal size="100" weight="medium">
              (+{allRoleNames.length - 1})
            </Text.Normal>
          </Tooltip.Alt>
        </Box>
      ) : (
        !hasRoles && (
          <Text.Normal size="100" weight="medium">
            {!isLoadingRoles ? (
              i18n.t(
                'shift_management.coverage_section.unassigned_shift.staff_requirements.staff_title'
              )
            ) : (
              <Spinner />
            )}
          </Text.Normal>
        )
      )}
    </Box>
  )
}

export { RolesDetails }
